// libs
import { History } from 'history'
import * as React from 'react'
import { AddToCalendarButton } from 'add-to-calendar-button-react'

// interfaces / constants
import { THUMBNAIL_SIZE_TYPE_MEDIUM } from 'src/components/thumbnail/thumbnail'
import { CLASS_PREFIX } from 'src/constants'
import { ITrackingType } from 'src/high_order_components/with_tracker_props'
import { IPost, POST_DISPLAY_MODE_DETAIL, PostType } from 'src/interfaces/posts'
import { PostDetailsObjType } from 'src/utils/reporting/events_tracking/events_tracking'

// classes
import NavigationItem from 'src/components/navigation_item/navigation_item'
import Author from 'src/components/post/shared/author/author'
import Ingredients from 'src/components/post/shared/ingredients/ingredients'
import ShareSegment from 'src/components/share/share_segment/share_segment'
import CommentsContainer from 'src/containers/comments'
import PremiumContent from 'src/containers/premium_content'
import { PostDetailsReactionsContainer } from 'src/containers/reactions'
import RelatedContent from 'src/containers/related_content'
import FollowButton from 'src/containers/smart_components/follow_button/follow_button'

// helpers
import ShapeSponsorsInfo from 'src/containers/smart_components/shape_sponsors/shape_sponsors_info/shape_sponsors_info'
import { textResources } from 'src/lang/de'
import ReactionOverview from '../shared/reactions/reaction_overview/reaction_overview'
import { filterIngredients } from './helpers'
import './post_detail.scss'

interface IPostDetails {
  addViewCount: () => void;
  post: IPost;
  history: History;
  tracking?: ITrackingType<PostDetailsObjType>;
  canReadPremiumContent: boolean;
}

const cls = CLASS_PREFIX + 'post__detail'

export default class PostDetails extends React.Component<IPostDetails, {}> {
  public constructor (props: IPostDetails) {
    super(props)
    // call add view when this component initializes
    props.addViewCount()
  }

  public render () {
    const { text } = textResources.addToCalender
    const { post, tracking, canReadPremiumContent } = this.props
    const {
      authorId,
      comments,
      externalUrl,
      id,
      ingredientIds,
      ingredients,
      meta,
      publishedAt,
      urls
    } = post
    const { fenced, premiumContent } = meta
    const { canBookmark, canComment, canShare } = meta.permissions
    const { similar } = meta.urls

    const postIngredients = ingredientIds.map((ingredientId) => ingredients[ingredientId]!)
      .filter(ingredient => filterIngredients(post, ingredient))

    const postType = this.props.post.meta.contentItemType
    const label = textResources.postDetail
    const blurType = fenced ? 'full' : !canReadPremiumContent && premiumContent ? 'gradient' : undefined

    const today = new Date()
    const startDate = new Date(ingredients?.eventStartEnd?.data?.startTime || today)
    const endDate = ingredients?.eventStartEnd?.data?.endTime
      ? new Date(ingredients?.eventStartEnd?.data?.endTime)
      : new Date(startDate)

    if (!ingredients?.eventStartEnd?.data?.endTime) {
      endDate.setHours(endDate.getHours() + 1)
    }

    const getBerlinDateTime = (date: number | Date | undefined) => {
      const berlinFormatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
      const [datePart, timePart] = berlinFormatter.format(date).split(' ')
      return [datePart, timePart]
    }

    const [eventStartDate, eventStartTime] = getBerlinDateTime(startDate)
    const [eventEndDate, eventEndTime] = getBerlinDateTime(endDate)

    return (
      <div className={cls}>
        <article className={cls + '__tile'}>
          <Ingredients
            ingredients={postIngredients}
            displayMode={POST_DISPLAY_MODE_DETAIL}
            blurred={blurType}
            postId={id}
            postType={postType}
          />

          {(postType === PostType.EVENT) && (
            <div className={cls + '__tile__footer'}>
              <div className={cls + '__author-more-section'}>
                <AddToCalendarButton
                  label={text}
                  name={ingredients.title?.data?.title}
                  location={ingredients?.map?.data?.markers[0]?.address}
                  options={['Apple', 'Google', 'iCal', 'MicrosoftTeams', 'Microsoft365', 'Outlook.com', 'Yahoo']}
                  startDate={eventStartDate}
                  endDate={eventEndDate}
                  startTime={eventStartTime}
                  endTime={eventEndTime}
                  timeZone="Europe/Berlin"
                ></AddToCalendarButton>
              </div>
            </div>
          )}

          <PremiumContent postUrl={urls.frontend} fencedContent={fenced} premiumContent={premiumContent}/>
          {externalUrl &&
            <div className={cls + '__source'}>
              <span className={cls + '__source__label'}>
                {postType === PostType.EVENT
                  ? label.tickets
                  : label.source}
              </span>
              <NavigationItem
                url={externalUrl}
                target="_blank"
                className={cls + '__source__link'}>
                {externalUrl}
              </NavigationItem>
            </div>}
          <div className={cls + '__tile__footer'}>
            <div className={cls + '__author-more-section'}>
              <Author
                displayMode={POST_DISPLAY_MODE_DETAIL}
                publishedAt={publishedAt}
                size={THUMBNAIL_SIZE_TYPE_MEDIUM}
                id={authorId}
              />
              <FollowButton followableId={authorId}/>
            </div>
            <ReactionOverview
              postId={id}
              displayMode={POST_DISPLAY_MODE_DETAIL}
              basePath={post.urls.frontend}
              disabled={!canBookmark}
            />
            <PostDetailsReactionsContainer id={id} canBookmark={canBookmark}/>
            {canShare &&
                  <div className={cls + '__share-wrapper'}>
                    <div className={cls + '__share'}>
                      <ShareSegment tracking={tracking} utmSource='post' url={urls.frontend}/>
                    </div>
                  </div>
            }
          </div>
          <div className={cls + '__footer'}>
            {!fenced && <CommentsContainer
              id={id}
              disabled={!canComment}
              commentable={post.commentable !== false}
              {...comments}
            />}
          </div>
          <ShapeSponsorsInfo key='shape_sponsors_info' placements={{ detailPage: true }}/>
          {similar && <div className={cls + '__related-content-wrapper'}>
            <RelatedContent path={similar}/>
          </div>}
        </article>
      </div>
    )
  }
}
